/**
 * LastWeek Tab Mockup - UX/UI Audit Enhancement Preview
 *
 * This is a MOCKUP showing proposed improvements to the LastWeek tab.
 * It uses sample data and is completely self-contained.
 *
 * PROPOSED ENHANCEMENTS (from UX/UI Audit):
 * 1. Sticky Section Navigation - Jump between sections quickly
 * 2. Collapsible Sections - Reduce scroll fatigue
 * 3. Weekly Trend Mini-Chart - Calls by day of week (NEW!)
 * 4. Merged WHY + Conversion Analysis - Consolidated view
 * 5. Week Summary at Top - Clear overview of completed week
 *
 * NOTE: LastWeek does NOT have "Today's Urgency" (it's historical)
 *
 * Created: December 3, 2025 for UX/UI Audit
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LabelList
} from 'recharts';

// ============================================
// SAMPLE DATA - Mimics real data patterns
// ============================================
const LAST_WEEK_DATES = {
  start: 'Nov 25',
  end: 'Nov 29',
  year: '2025'
};

const SAMPLE_FINAL_METRICS = {
  totals: {
    calls: 278,
    sms: 156,
    apps: 11,
    approved: 8,
    funded: 4,
    revenue: 142000,
    connected: 89,
    activityCount: 382,
    totalTalkTime: 178
  },
  weeklyTargets: {
    calls: 275,
    sms: 150,
    apps: 10,
    revenue: 115000
  },
  achievements: {
    calls: 101.1,
    sms: 104.0,
    apps: 110.0,
    revenue: 123.5
  },
  conversions: {
    callsToApps: 4.0,
    smsToApps: 7.1,
    appsToApproved: 72.7,
    approvedToFunded: 50.0,
    appsToFunded: 36.4
  }
};

const SAMPLE_DAILY_DATA = [
  { day: 'Mon', date: '11/25', calls: 62, sms: 34, connected: 20, apps: 3, approved: 2, funded: 1, revenue: 38000, activityCount: 84 },
  { day: 'Tue', date: '11/26', calls: 58, sms: 32, connected: 18, apps: 2, approved: 2, funded: 1, revenue: 36000, activityCount: 78 },
  { day: 'Wed', date: '11/27', calls: 54, sms: 28, connected: 17, apps: 2, approved: 1, funded: 1, revenue: 32000, activityCount: 72 },
  { day: 'Thu', date: '11/28', calls: 48, sms: 30, connected: 16, apps: 2, approved: 2, funded: 0, revenue: 0, activityCount: 68 },
  { day: 'Fri', date: '11/29', calls: 56, sms: 32, connected: 18, apps: 2, approved: 1, funded: 1, revenue: 36000, activityCount: 80 }
];

const SAMPLE_REP_DATA = [
  { rep: 'John', calls: 78, sms: 42, apps: 4, approved: 3, funded: 2, revenue: 62000, callToApp: 5.1, status: 'excellent' },
  { rep: 'Sarah', calls: 72, sms: 40, apps: 3, approved: 2, funded: 1, revenue: 38000, callToApp: 4.2, status: 'good' },
  { rep: 'Mike', calls: 68, sms: 38, apps: 2, approved: 2, funded: 1, revenue: 28000, callToApp: 2.9, status: 'ok' },
  { rep: 'Jane', calls: 60, sms: 36, apps: 2, approved: 1, funded: 0, revenue: 14000, callToApp: 3.3, status: 'needs_help' }
];

// For the new mini-chart
const SAMPLE_TREND_DATA = SAMPLE_DAILY_DATA.map(d => ({
  day: d.day,
  calls: d.calls,
  sms: d.sms,
  apps: d.apps
}));

// Per-rep data for daily breakdown expandable rows - ALL columns to match parent table
const SAMPLE_REP_DAILY_DATA = {
  'Mon': [
    { rep: 'John', calls: 18, sms: 10, connected: 6, apps: 1, approved: 1, funded: 0, revenue: 0, activityCount: 24, status: 'good' },
    { rep: 'Sarah', calls: 16, sms: 9, connected: 5, apps: 1, approved: 0, funded: 1, revenue: 38000, activityCount: 21, status: 'good' },
    { rep: 'Mike', calls: 14, sms: 8, connected: 5, apps: 1, approved: 1, funded: 0, revenue: 0, activityCount: 20, status: 'good' },
    { rep: 'Jane', calls: 14, sms: 7, connected: 4, apps: 0, approved: 0, funded: 0, revenue: 0, activityCount: 19, status: 'ok' }
  ],
  'Tue': [
    { rep: 'John', calls: 17, sms: 9, connected: 5, apps: 1, approved: 1, funded: 1, revenue: 36000, activityCount: 22, status: 'good' },
    { rep: 'Sarah', calls: 15, sms: 8, connected: 5, apps: 0, approved: 1, funded: 0, revenue: 0, activityCount: 19, status: 'ok' },
    { rep: 'Mike', calls: 14, sms: 8, connected: 4, apps: 1, approved: 0, funded: 0, revenue: 0, activityCount: 18, status: 'good' },
    { rep: 'Jane', calls: 12, sms: 7, connected: 4, apps: 0, approved: 0, funded: 0, revenue: 0, activityCount: 19, status: 'ok' }
  ],
  'Wed': [
    { rep: 'John', calls: 16, sms: 8, connected: 5, apps: 1, approved: 0, funded: 1, revenue: 32000, activityCount: 20, status: 'good' },
    { rep: 'Sarah', calls: 14, sms: 7, connected: 4, apps: 0, approved: 1, funded: 0, revenue: 0, activityCount: 17, status: 'ok' },
    { rep: 'Mike', calls: 12, sms: 7, connected: 4, apps: 1, approved: 0, funded: 0, revenue: 0, activityCount: 17, status: 'good' },
    { rep: 'Jane', calls: 12, sms: 6, connected: 4, apps: 0, approved: 0, funded: 0, revenue: 0, activityCount: 18, status: 'ok' }
  ],
  'Thu': [
    { rep: 'John', calls: 14, sms: 9, connected: 5, apps: 1, approved: 1, funded: 0, revenue: 0, activityCount: 20, status: 'good' },
    { rep: 'Sarah', calls: 12, sms: 8, connected: 4, apps: 0, approved: 1, funded: 0, revenue: 0, activityCount: 17, status: 'ok' },
    { rep: 'Mike', calls: 12, sms: 7, connected: 4, apps: 1, approved: 0, funded: 0, revenue: 0, activityCount: 16, status: 'good' },
    { rep: 'Jane', calls: 10, sms: 6, connected: 3, apps: 0, approved: 0, funded: 0, revenue: 0, activityCount: 15, status: 'behind' }
  ],
  'Fri': [
    { rep: 'John', calls: 16, sms: 9, connected: 5, apps: 1, approved: 0, funded: 1, revenue: 36000, activityCount: 21, status: 'good' },
    { rep: 'Sarah', calls: 15, sms: 8, connected: 5, apps: 1, approved: 1, funded: 0, revenue: 0, activityCount: 19, status: 'good' },
    { rep: 'Mike', calls: 13, sms: 8, connected: 4, apps: 0, approved: 0, funded: 0, revenue: 0, activityCount: 21, status: 'ok' },
    { rep: 'Jane', calls: 12, sms: 7, connected: 4, apps: 0, approved: 0, funded: 0, revenue: 0, activityCount: 19, status: 'ok' }
  ]
};

// ============================================
// MAIN MOCKUP COMPONENT
// ============================================
export default function LastWeekTabMockup() {
  const [activeSection, setActiveSection] = useState('summary');
  const [collapsedSections, setCollapsedSections] = useState({});
  const [expandedDay, setExpandedDay] = useState(null);

  // Set unique browser tab title
  useEffect(() => {
    document.title = '🎨 MOCKUP: LastWeek Tab';
  }, []);

  // Refs for scrolling
  const sectionRefs = {
    summary: useRef(null),
    conversions: useRef(null),
    daily: useRef(null),
    why: useRef(null)
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(value);

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  // Determine overall status
  const avgAchievement = (SAMPLE_FINAL_METRICS.achievements.calls +
    SAMPLE_FINAL_METRICS.achievements.sms +
    SAMPLE_FINAL_METRICS.achievements.apps +
    SAMPLE_FINAL_METRICS.achievements.revenue) / 4;
  const overallStatus = avgAchievement >= 100 ? 'success' : avgAchievement >= 85 ? 'close' : 'missed';

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      {/* Header */}
      <div className="dashboard-header">
        <h1>Sales Performance Dashboard</h1>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          backgroundColor: '#fef3c7',
          padding: '8px 16px',
          borderRadius: '6px',
          border: '2px solid #f59e0b'
        }}>
          <span style={{ fontSize: '18px' }}>🎨</span>
          <span style={{ fontWeight: 600, color: '#92400e' }}>
            MOCKUP MODE - Proposed LastWeek Tab Enhancements
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <nav className="tab-navigation">
        <button>Overview</button>
        <button>This Week</button>
        <button className="active">Last Week</button>
        <button>Historical</button>
      </nav>

      {/* ============================================ */}
      {/* KNOWN BUG BANNER                            */}
      {/* ============================================ */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#fef2f2',
        border: '2px solid #ef4444',
        borderRadius: '8px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <span style={{ fontSize: '24px' }}>🐛</span>
        <div>
          <strong style={{ color: '#991b1b' }}>BUG-001 (To Fix in Implementation):</strong>
          <span style={{ color: '#7f1d1d', marginLeft: '8px' }}>
            Activity Monitor uses 7-day window instead of 5-day business days (Mon-Fri only)
          </span>
        </div>
      </div>

      {/* ============================================ */}
      {/* ENHANCED: Sticky Section Navigation         */}
      {/* ============================================ */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        padding: '12px 0',
        borderBottom: '2px solid #e2e8f0',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginRight: '8px' }}>
            JUMP TO:
          </span>
          {[
            { id: 'summary', label: '📊 Week Summary', icon: '1' },
            { id: 'conversions', label: '📈 Conversions & WHY', icon: '2' },
            { id: 'daily', label: '📅 Daily Breakdown', icon: '3' },
            { id: 'why', label: '👥 Rep Analysis', icon: '4' }
          ].map(section => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: activeSection === section.id ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                backgroundColor: activeSection === section.id ? '#eff6ff' : 'white',
                color: activeSection === section.id ? '#1e40af' : '#475569',
                fontWeight: activeSection === section.id ? 600 : 500,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {section.label}
            </button>
          ))}

          <div style={{
            marginLeft: 'auto',
            padding: '6px 12px',
            backgroundColor: '#d1fae5',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#065f46',
            fontWeight: 600
          }}>
            ✨ NEW: Sticky section navigation
          </div>
        </div>
      </div>

      <main className="dashboard-content">
        <div className="last-week-tab">
          <h2>📅 Last Week - Team Performance Review</h2>
          <p className="tab-description">
            Week of {LAST_WEEK_DATES.start} - {LAST_WEEK_DATES.end}, {LAST_WEEK_DATES.year} (Completed)
          </p>

          {/* ============================================ */}
          {/* SECTION 1: WEEK SUMMARY                     */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.summary}
            style={{
              scrollMarginTop: '80px',
              marginTop: '24px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('summary')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>📊 Week at a Glance</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Final weekly performance summary
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 600,
                  backgroundColor: overallStatus === 'success' ? '#d1fae5' : overallStatus === 'close' ? '#fef3c7' : '#fee2e2',
                  color: overallStatus === 'success' ? '#065f46' : overallStatus === 'close' ? '#92400e' : '#991b1b'
                }}>
                  {overallStatus === 'success' ? '🟢 Goals Met' : overallStatus === 'close' ? '🟡 Close' : '🔴 Missed'}
                </span>
                <span style={{
                  fontSize: '20px',
                  color: '#64748b',
                  transform: collapsedSections.summary ? 'rotate(-90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>
                  ▼
                </span>
              </div>
            </div>

            {!collapsedSections.summary && (
              <>
                {/* Summary Cards */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <SummaryCardWithAchievement
                    icon="📞"
                    title="Calls"
                    actual={SAMPLE_FINAL_METRICS.totals.calls}
                    target={SAMPLE_FINAL_METRICS.weeklyTargets.calls}
                    achievement={SAMPLE_FINAL_METRICS.achievements.calls}
                  />
                  <SummaryCardWithAchievement
                    icon="💬"
                    title="SMS"
                    actual={SAMPLE_FINAL_METRICS.totals.sms}
                    target={SAMPLE_FINAL_METRICS.weeklyTargets.sms}
                    achievement={SAMPLE_FINAL_METRICS.achievements.sms}
                  />
                  <SummaryCardWithAchievement
                    icon="📋"
                    title="Apps"
                    actual={SAMPLE_FINAL_METRICS.totals.apps}
                    target={SAMPLE_FINAL_METRICS.weeklyTargets.apps}
                    achievement={SAMPLE_FINAL_METRICS.achievements.apps}
                  />
                  <SummaryCardWithAchievement
                    icon="💰"
                    title="Revenue"
                    actual={formatCurrency(SAMPLE_FINAL_METRICS.totals.revenue)}
                    target={formatCurrency(SAMPLE_FINAL_METRICS.weeklyTargets.revenue)}
                    achievement={SAMPLE_FINAL_METRICS.achievements.revenue}
                  />
                </div>

                {/* ENHANCED: Weekly Trend Mini-Chart */}
                <div style={{
                  padding: '20px',
                  background: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  marginBottom: '24px',
                  position: 'relative'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>📈 Daily Activity Trend</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <span style={{
                        padding: '4px 8px',
                        backgroundColor: '#d1fae5',
                        borderRadius: '4px',
                        fontSize: '10px',
                        color: '#065f46',
                        fontWeight: 600
                      }}>
                        ✨ NEW: Trend visualization
                      </span>
                      <span style={{
                        padding: '4px 8px',
                        backgroundColor: '#dbeafe',
                        borderRadius: '4px',
                        fontSize: '10px',
                        color: '#1e40af',
                        fontWeight: 600
                      }}>
                        🏷️ 12/5 LABELS
                      </span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={SAMPLE_TREND_DATA}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="calls" fill="#3b82f6" name="Calls" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="calls" position="top" style={{ fontSize: 11, fill: '#3b82f6', fontWeight: 600 }} />
                      </Bar>
                      <Bar dataKey="sms" fill="#9333ea" name="SMS" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="sms" position="top" style={{ fontSize: 11, fill: '#9333ea', fontWeight: 600 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 2: CONVERSIONS & WHY (MERGED!)      */}
          {/* ENHANCED: Combined for less duplication     */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.conversions}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('conversions')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>📈 Conversions & WHY Analysis</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  How well did we convert, and why?
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  padding: '4px 8px',
                  backgroundColor: '#d1fae5',
                  borderRadius: '4px',
                  fontSize: '10px',
                  color: '#065f46',
                  fontWeight: 600
                }}>
                  ✨ MERGED: Was 2 sections
                </span>
                <span style={{
                  fontSize: '20px',
                  color: '#64748b',
                  transform: collapsedSections.conversions ? 'rotate(-90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>
                  ▼
                </span>
              </div>
            </div>

            {!collapsedSections.conversions && (
              <>
                {/* Conversion Rates */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <ConversionCard
                    label="Call → App"
                    rate={SAMPLE_FINAL_METRICS.conversions.callsToApps}
                    detail={`${SAMPLE_FINAL_METRICS.totals.apps} apps from ${SAMPLE_FINAL_METRICS.totals.calls} calls`}
                    benchmark={3.5}
                  />
                  <ConversionCard
                    label="SMS → App"
                    rate={SAMPLE_FINAL_METRICS.conversions.smsToApps}
                    detail={`${SAMPLE_FINAL_METRICS.totals.apps} apps from ${SAMPLE_FINAL_METRICS.totals.sms} SMS`}
                    benchmark={6.0}
                  />
                  <ConversionCard
                    label="App → Approved"
                    rate={SAMPLE_FINAL_METRICS.conversions.appsToApproved}
                    detail={`${SAMPLE_FINAL_METRICS.totals.approved} from ${SAMPLE_FINAL_METRICS.totals.apps} apps`}
                    benchmark={65.0}
                  />
                  <ConversionCard
                    label="Approved → Funded"
                    rate={SAMPLE_FINAL_METRICS.conversions.approvedToFunded}
                    detail={`${SAMPLE_FINAL_METRICS.totals.funded} from ${SAMPLE_FINAL_METRICS.totals.approved} approved`}
                    benchmark={50.0}
                  />
                  <ConversionCard
                    label="App → Funded"
                    rate={SAMPLE_FINAL_METRICS.conversions.appsToFunded}
                    detail={`${SAMPLE_FINAL_METRICS.totals.funded} from ${SAMPLE_FINAL_METRICS.totals.apps} apps`}
                    benchmark={35.0}
                  />
                </div>

                {/* The Funnel Visualization */}
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '12px',
                  marginBottom: '24px'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 600, color: '#1e40af' }}>
                    THE FUNNEL - Week Summary
                  </h4>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}>
                    <FunnelStage label="Calls" value={SAMPLE_FINAL_METRICS.totals.calls} color="#3b82f6" />
                    <FunnelArrow rate={formatPercent(SAMPLE_FINAL_METRICS.conversions.callsToApps)} />
                    <FunnelStage label="Apps In" value={SAMPLE_FINAL_METRICS.totals.apps} color="#10b981" />
                    <FunnelArrow rate={formatPercent(SAMPLE_FINAL_METRICS.conversions.appsToApproved)} />
                    <FunnelStage label="Approved" value={SAMPLE_FINAL_METRICS.totals.approved} color="#8b5cf6" />
                    <FunnelArrow rate={formatPercent(SAMPLE_FINAL_METRICS.conversions.approvedToFunded)} />
                    <FunnelStage label="Funded" value={SAMPLE_FINAL_METRICS.totals.funded} color="#f59e0b" />
                    <FunnelArrow rate="→" />
                    <FunnelStage label="Revenue" value={`$${Math.round(SAMPLE_FINAL_METRICS.totals.revenue / 1000)}K`} color="#10b981" />
                  </div>
                </div>

                {/* Efficiency Metrics */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Efficiency Metrics</h4>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '4px',
                      fontSize: '10px',
                      color: '#92400e',
                      fontWeight: 600,
                      border: '1px solid #f59e0b'
                    }}>
                      🔍 DATA REVIEW: Needs pack_source data
                    </span>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '12px'
                  }}>
                    <MetricCard
                      label="Avg Deal Size"
                      value={formatCurrency(SAMPLE_FINAL_METRICS.totals.revenue / SAMPLE_FINAL_METRICS.totals.funded)}
                      detail={`${formatCurrency(SAMPLE_FINAL_METRICS.totals.revenue)} ÷ ${SAMPLE_FINAL_METRICS.totals.funded} deals`}
                    />
                    <MetricCard
                      label="Calls per App"
                      value={(SAMPLE_FINAL_METRICS.totals.calls / SAMPLE_FINAL_METRICS.totals.apps).toFixed(1)}
                      detail={`${SAMPLE_FINAL_METRICS.totals.calls} calls ÷ ${SAMPLE_FINAL_METRICS.totals.apps} apps`}
                    />
                    <MetricCard
                      label="SMS per App"
                      value={(SAMPLE_FINAL_METRICS.totals.sms / SAMPLE_FINAL_METRICS.totals.apps).toFixed(1)}
                      detail={`${SAMPLE_FINAL_METRICS.totals.sms} SMS ÷ ${SAMPLE_FINAL_METRICS.totals.apps} apps`}
                    />
                    <MetricCard
                      label="Talk Time"
                      value={`${Math.floor(SAMPLE_FINAL_METRICS.totals.totalTalkTime / 60)}h ${SAMPLE_FINAL_METRICS.totals.totalTalkTime % 60}m`}
                      detail={`${SAMPLE_FINAL_METRICS.totals.totalTalkTime} total minutes`}
                    />
                  </div>
                </div>
              </>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 3: DAILY BREAKDOWN                  */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.daily}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('daily')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>📊 Daily Breakdown</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Day-by-day performance with rep drill-down
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.daily ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.daily && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Day</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Calls</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>SMS</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Connected</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Apps</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Approved</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Funded</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Revenue</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SAMPLE_DAILY_DATA.map((day, idx) => (
                      <React.Fragment key={day.day}>
                        <tr
                          style={{
                            borderBottom: '1px solid #e2e8f0',
                            backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb',
                            cursor: 'pointer'
                          }}
                          onClick={() => setExpandedDay(expandedDay === day.day ? null : day.day)}
                        >
                          <td style={{ padding: '12px', fontWeight: 600 }}>
                            {day.day} <span style={{ color: '#64748b', fontWeight: 400 }}>{day.date}</span>
                            <span style={{ marginLeft: '8px', color: '#94a3b8' }}>{expandedDay === day.day ? '▼' : '▶'}</span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.calls}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.sms}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.connected}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.apps}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.approved}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.funded}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(day.revenue)}</td>
                          <td style={{
                            padding: '12px',
                            textAlign: 'right',
                            fontWeight: 600,
                            backgroundColor: day.activityCount >= 75 ? '#d1fae5' : day.activityCount >= 60 ? '#fef3c7' : '#fee2e2'
                          }}>
                            {day.activityCount}
                          </td>
                        </tr>
                        {/* Expandable Per-Rep Breakdown */}
                        {expandedDay === day.day && SAMPLE_REP_DAILY_DATA[day.day] && (
                          <tr>
                            <td colSpan="10" style={{ padding: '0', backgroundColor: '#f8fafc' }}>
                              <div style={{
                                padding: '16px 24px',
                                borderLeft: '4px solid #3b82f6'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                  <span style={{ fontWeight: 600, color: '#1e293b' }}>👥 Per-Rep Breakdown for {day.day}</span>
                                  <span style={{
                                    padding: '2px 8px',
                                    backgroundColor: '#d1fae5',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    color: '#065f46',
                                    fontWeight: 600
                                  }}>
                                    ✨ NEW
                                  </span>
                                </div>
                                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Rep</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Calls</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>SMS</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Connected</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Apps</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Approved</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Funded</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Revenue</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Activity</th>
                                      <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#64748b' }}>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {SAMPLE_REP_DAILY_DATA[day.day].map(rep => (
                                      <tr key={rep.rep} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <td style={{ padding: '8px', fontWeight: 500 }}>{rep.rep}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.calls}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.sms}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.connected}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.apps}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.approved}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.funded}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>${rep.revenue > 0 ? (rep.revenue / 1000).toFixed(0) + 'K' : '0'}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.activityCount}</td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>
                                          <span style={{
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            backgroundColor: rep.status === 'good' ? '#d1fae5' : rep.status === 'ok' ? '#fef3c7' : '#fee2e2',
                                            color: rep.status === 'good' ? '#065f46' : rep.status === 'ok' ? '#92400e' : '#991b1b'
                                          }}>
                                            {rep.status === 'good' ? '✅ Good' : rep.status === 'ok' ? '⚠️ OK' : '🔴 Behind'}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    {/* Total Row */}
                    <tr style={{ backgroundColor: '#f1f5f9', fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                      <td style={{ padding: '12px' }}>TOTAL</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_METRICS.totals.calls}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_METRICS.totals.sms}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_METRICS.totals.connected}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_METRICS.totals.apps}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_METRICS.totals.approved}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_METRICS.totals.funded}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(SAMPLE_FINAL_METRICS.totals.revenue)}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_METRICS.totals.activityCount}</td>
                    </tr>
                    {/* Target Row */}
                    <tr style={{ backgroundColor: '#f8fafc', color: '#64748b' }}>
                      <td style={{ padding: '12px' }}>TARGET</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_METRICS.weeklyTargets.calls}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_METRICS.weeklyTargets.sms}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>-</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_METRICS.weeklyTargets.apps}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>-</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>-</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(SAMPLE_FINAL_METRICS.weeklyTargets.revenue)}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>375</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 4: REP ANALYSIS                     */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.why}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('why')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>👥 Rep Performance</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Individual rep breakdown for the week
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.why ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.why && (
              <>
                {/* Rep Performance Table */}
                <div style={{ marginBottom: '24px' }}>
                  <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ textAlign: 'left', padding: '10px 12px' }}>Rep</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Calls</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>SMS</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Apps</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Approved</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Funded</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Revenue</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Call→App</th>
                        <th style={{ textAlign: 'center', padding: '10px 12px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SAMPLE_REP_DATA.map((rep, idx) => (
                        <tr key={rep.rep} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                          <td style={{ padding: '10px 12px', fontWeight: 500 }}>{rep.rep}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.calls}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.sms}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.apps}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.approved}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.funded}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(rep.revenue)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.callToApp}%</td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor:
                                rep.status === 'excellent' ? '#d1fae5' :
                                rep.status === 'good' ? '#dbeafe' :
                                rep.status === 'ok' ? '#fef3c7' : '#fee2e2',
                              color:
                                rep.status === 'excellent' ? '#065f46' :
                                rep.status === 'good' ? '#1e40af' :
                                rep.status === 'ok' ? '#92400e' : '#991b1b'
                            }}>
                              {rep.status === 'excellent' ? '⭐ Excellent' :
                               rep.status === 'good' ? '✅ Good' :
                               rep.status === 'ok' ? '⚠️ OK' : '❗ Needs Help'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Rep Performance Chart */}
                <div style={{
                  padding: '20px',
                  background: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Call→App Conversion by Rep</h4>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#dbeafe',
                      borderRadius: '4px',
                      fontSize: '10px',
                      color: '#1e40af',
                      fontWeight: 600
                    }}>
                      🏷️ 12/5 LABELS
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={SAMPLE_REP_DATA} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" domain={[0, 6]} stroke="#64748b" fontSize={12} tickFormatter={(v) => `${v}%`} />
                      <YAxis type="category" dataKey="rep" stroke="#64748b" fontSize={12} />
                      <Tooltip formatter={(value) => [`${value}%`, 'Call→App Rate']} />
                      <Bar dataKey="callToApp" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                        <LabelList dataKey="callToApp" position="right" style={{ fontSize: 11, fill: '#3b82f6', fontWeight: 600 }} formatter={(v) => `${v}%`} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#64748b'
                  }}>
                    Team Average: <strong style={{ color: '#1e293b' }}>{formatPercent(SAMPLE_FINAL_METRICS.conversions.callsToApps)}</strong>
                    <span style={{ marginLeft: '16px' }}>|</span>
                    <span style={{ marginLeft: '16px' }}>6-Mo Benchmark: <strong style={{ color: '#10b981' }}>3.5%</strong></span>
                  </div>
                </div>
              </>
            )}
          </section>

        </div>
      </main>

      {/* Enhancement Legend */}
      <div style={{
        marginTop: '32px',
        padding: '20px',
        backgroundColor: '#eff6ff',
        borderRadius: '8px',
        border: '2px solid #3b82f6'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#1e40af' }}>
          Enhancements in This Mockup:
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          <EnhancementItem
            title="1. Sticky Section Navigation"
            description="Jump between sections quickly without scrolling."
          />
          <EnhancementItem
            title="2. Collapsible Sections"
            description="Click section headers to collapse/expand."
          />
          <EnhancementItem
            title="3. Weekly Trend Mini-Chart"
            description="NEW: Visualize daily activity trends across the week."
          />
          <EnhancementItem
            title="4. Merged WHY + Conversions"
            description="Combined conversion rates with efficiency metrics."
          />
          <EnhancementItem
            title="5. Achievement Badges"
            description="Clear visual indicators for goal attainment."
          />
        </div>
      </div>
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function SummaryCardWithAchievement({ icon, title, actual, target, achievement }) {
  const status = achievement >= 100 ? 'success' : achievement >= 85 ? 'close' : 'behind';

  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '24px', marginBottom: '8px' }}>{icon}</div>
      <h4 style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>{title}</h4>
      <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>{actual} / {target}</div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 600,
          backgroundColor: status === 'success' ? '#d1fae5' : status === 'close' ? '#fef3c7' : '#fee2e2',
          color: status === 'success' ? '#065f46' : status === 'close' ? '#92400e' : '#991b1b'
        }}>
          {achievement.toFixed(0)}%
        </span>
        <span style={{ fontSize: '12px', color: '#64748b' }}>
          {status === 'success' ? '✅ Met' : status === 'close' ? '🟡 Close' : '🔴 Behind'}
        </span>
      </div>
    </div>
  );
}

function ConversionCard({ label, rate, detail, benchmark }) {
  const diff = rate - benchmark;
  const isUp = diff >= 0;

  return (
    <div style={{
      padding: '12px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>{rate.toFixed(1)}%</div>
      <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '6px' }}>{detail}</div>
      <div style={{ fontSize: '11px', color: isUp ? '#10b981' : '#ef4444', fontWeight: 600 }}>
        {isUp ? '↗' : '↘'} {isUp ? '+' : ''}{diff.toFixed(1)}pp vs benchmark
      </div>
    </div>
  );
}

function MetricCard({ label, value, detail }) {
  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '6px' }}>{value}</div>
      <div style={{ fontSize: '12px', color: '#94a3b8' }}>{detail}</div>
    </div>
  );
}

function FunnelStage({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', minWidth: '60px' }}>
      <div style={{
        backgroundColor: color,
        color: 'white',
        padding: '10px 14px',
        borderRadius: '8px',
        fontWeight: 700,
        fontSize: '18px',
        marginBottom: '6px'
      }}>
        {value}
      </div>
      <div style={{ fontSize: '10px', fontWeight: 600, color: '#475569', textTransform: 'uppercase' }}>{label}</div>
    </div>
  );
}

function FunnelArrow({ rate }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontSize: '20px', color: '#cbd5e1' }}>→</div>
      <div style={{ fontSize: '10px', fontWeight: 600, color: '#64748b' }}>{rate}</div>
    </div>
  );
}

function EnhancementItem({ title, description }) {
  return (
    <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '6px' }}>
      <strong style={{ color: '#10b981' }}>{title}</strong>
      <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#64748b' }}>{description}</p>
    </div>
  );
}
